import { useState, useMemo, useCallback } from 'react';
import type { Incident, IncidentsFilters, FilterType, SortType } from '../types';

interface UseIncidentFiltersResult {
    filters: IncidentsFilters;
    setFilterType: (type: FilterType) => void;
    setSortType: (sort: SortType) => void;
    setSearch: (search: string) => void;
    filteredIncidents: Incident[];
    clearFilters: () => void;
}

const defaultFilters: IncidentsFilters = {
    type: 'all',
    sort: 'newest',
    search: ''
};

export const useIncidentFilters = (incidents: Incident[]): UseIncidentFiltersResult => {
    const [filters, setFilters] = useState<IncidentsFilters>(defaultFilters);

    const setFilterType = useCallback((type: FilterType) => {
        setFilters(prev => ({ ...prev, type }));
    }, []);

    const setSortType = useCallback((sort: SortType) => {
        setFilters(prev => ({ ...prev, sort }));
    }, []);

    const setSearch = useCallback((search: string) => {
        setFilters(prev => ({ ...prev, search }));
    }, []);

    const clearFilters = useCallback(() => {
        setFilters(defaultFilters);
    }, []);

    const filteredIncidents = useMemo(() => {
        let result = [...incidents];

        if (filters.type !== 'all') {
            if (filters.type === 'active') {
                result = result.filter(i => i.status === 'active' || i.status === 'investigating');
            } else if (filters.type === 'critical') {
                result = result.filter(i => i.type === 'critical');
            } else if (filters.type === 'warning') {
                result = result.filter(i => i.type === 'warning');
            } else if (filters.type === 'resolved') {
                result = result.filter(i => i.status === 'resolved' || i.status === 'archived');
            }
        }

        if (filters.search.trim()) {
            const searchLower = filters.search.toLowerCase();
            result = result.filter(i => 
                i.title.toLowerCase().includes(searchLower) ||
                i.description.toLowerCase().includes(searchLower) ||
                i.meta.siteName.toLowerCase().includes(searchLower) ||
                i.meta.siteUrl.toLowerCase().includes(searchLower)
            );
        }

        result.sort((a, b) => {
            switch (filters.sort) {
                case 'newest':
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                case 'oldest':
                    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                case 'severity':
                    return a.severity - b.severity;
                case 'duration':
                    return (b.meta.duration || 0) - (a.meta.duration || 0);
                default:
                    return 0;
            }
        });

        return result;
    }, [incidents, filters]);

    return {
        filters,
        setFilterType,
        setSortType,
        setSearch,
        filteredIncidents,
        clearFilters
    };
};